import { useState, useMemo, useEffect } from "react";
import { useSubmitGuard } from "@/hooks/useFormSafety";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { Link, useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useLeads } from "@/hooks/useLeads";
import {
  usePipelineLeadsKanban,
  usePipelineLeadsPage,
  usePipelineRealtimeRefreshSignal,
  usePipelineSpeedToLeadSummary,
} from "@/hooks/usePipelineServerPaging";
import { useRole } from "@/data/RoleContext";
import { useAuth } from "@/contexts/AuthContext";
import { getCurrentRepName } from "@/lib/getCurrentRepName";
import { usePipelineConfig } from "@/data/PipelineConfigContext";
import { serializeStageHistory, serializeActivities } from "@/mappers/lead.mapper";
import { type PipelineStage, type Lead } from "@/data/pipelineTypes";
import { WON_OUTCOME_LABELS, WON_OUTCOME_COLORS } from "@/data/opportunityTypes";
import { PROJECT_TYPES } from "@/data/types";
import { processIncomingLead, DEFAULT_INTEGRITY_CONFIG } from "@/data/leadIntegrityEngine";
import { processAutomationEvent, DEFAULT_AUTOMATION_RULES, type AutomationEvent } from "@/data/automationEngine";
import {
  calculateResponseMetrics, calculateLeadPriority, calculateSpeedToLeadSummary,
  getRepPerformance, recommendAssignment,
  SLA_BADGE_CONFIG, type SLAStatus, type LeadResponseMetrics, type LeadPriorityScore,
} from "@/data/speedToLead";
import { scoreLeadConversion, TEMP_CONFIG } from "@/data/conversionIntelligence";
import { PipelineStageManager } from "@/components/PipelineStageManager";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import {
  Plus, Search, LayoutGrid, List, Phone, Mail, MapPin, Calendar,
  ArrowRight, FileText, Briefcase, GripVertical, User, Clock,
  MessageSquare, CalendarPlus, CheckCircle2, AlertTriangle,
  Zap, Timer, ShieldAlert, TrendingUp, UserPlus, Lock,
} from "lucide-react";
import { StageOverrideDialog, canOverrideStage } from "@/components/StageOverrideDialog";
import { cn } from "@/lib/utils";
import { CockpitPage, CockpitMasthead, HeroMetric, HeroDivider, CockpitKpiStrip, CockpitFilterBar, CockpitSearch } from "@/components/cockpit/CockpitShell";
import { isGrowthChannelSource, getGrowthChannelBadge } from "@/lib/growthChannel";
import { toast } from "sonner";
import { useActionPermissions } from "@/hooks/useActionPermissions";
import { format, differenceInDays } from "date-fns";
import { QuoteService } from "@/services/quote.service";
import { LeadService } from "@/services/lead.service";
import { AppointmentService } from "@/services/appointment.service";
import { useUserResolver } from "@/hooks/useUserResolver";
import { supabase } from "@/lib/supabase";
import { isUuid } from "@/lib/uuid";
import { PIPELINE_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { formatPhoneNumber } from "@/lib/phoneFormat";

type ViewMode = "kanban" | "list";

const LEAD_SOURCES = ["Google Ads", "Facebook Ads", "Referral", "Home Show", "Website", "TV Ad", "Angi", "HomeAdvisor", "Direct Mail", "Other"];
const KANBAN_STAGE_PAGE_SIZE = 10;

/* ═══════ ENRICHED LEAD TYPE ═══════ */

type EnrichedLead = Lead & {
  responseMetrics: LeadResponseMetrics;
  priority: LeadPriorityScore;
  temperature: import("@/data/conversionIntelligence").LeadTemperature;
};

function getLeadProjectLabel(lead: Lead): string {
  const direct = String(lead.projectType ?? "").trim();
  if (direct && direct !== "Other") return direct;

  const cfg = (lead.projectConfig ?? {}) as Record<string, unknown>;
  const cfgProject =
    String(
      cfg.projectType ??
      cfg.project_type ??
      cfg.projectCategory ??
      cfg.consultationCategory ??
      cfg.scopeCategory ??
      "",
    ).trim();
  if (cfgProject) return cfgProject;

  return direct || "Other";
}

function getLeadLocationLabel(lead: Lead): string {
  const city = String(lead.city ?? "").trim();
  const state = String(lead.state ?? "").trim();
  const zip = String(lead.zip ?? "").trim();
  const street = String(lead.address ?? "").trim();

  if (city && state) return `${city}, ${state}`;
  if (city) return zip ? `${city} ${zip}` : city;
  if (state) return zip ? `${state} ${zip}` : state;
  if (street) return street;
  if (zip) return zip;
  return "—";
}

const APPOINTMENT_BLOCK_MS = 4 * 60 * 60 * 1000;

function hasBlockedWindowOverlap(existingIso: string, candidateIso: string): boolean {
  const existing = new Date(existingIso);
  const candidate = new Date(candidateIso);
  if (Number.isNaN(existing.getTime()) || Number.isNaN(candidate.getTime())) return false;
  const existingStart = existing.getTime();
  const existingEnd = existingStart + APPOINTMENT_BLOCK_MS;
  const candidateStart = candidate.getTime();
  const candidateEnd = candidateStart + APPOINTMENT_BLOCK_MS;
  return existingStart < candidateEnd && candidateStart < existingEnd;
}

export default function Pipeline() {
  const { conversations, messages, leadSources, isRefreshing } = useAppData();
  const { role } = useRole();
  const { user } = useAuth();
  const { getVisibleStages, getStageColor } = usePipelineConfig();
  const actionPerms = useActionPermissions();
  const navigate = useNavigate();
  const serverPagingEnabled = PIPELINE_SERVER_PAGING_ENABLED;
  const realtimeRefreshToken = usePipelineRealtimeRefreshSignal(serverPagingEnabled);
  const [viewMode, setViewMode] = useState<ViewMode>("kanban");
  const [search, setSearch] = useState("");
  const [repFilter, setRepFilter] = useState("all");
  const [serverListPage, setServerListPage] = useState(0);
  const [serverListPageSize, setServerListPageSize] = useState(25);
  const [showNewLead, setShowNewLead] = useState(false);
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  const [pendingDrop, setPendingDrop] = useState<{ leadId: string; stage: PipelineStage; fromStage: PipelineStage; leadName: string } | null>(null);
  const canOverride = canOverrideStage(role);
  const [scheduleLeadId, setScheduleLeadId] = useState<string | null>(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("10:00");
  const [scheduleRep, setScheduleRep] = useState("");
  const [showPriorityQueue, setShowPriorityQueue] = useState(true);
  const { isSubmitting: isCreatingLead, guard: guardCreateLead } = useSubmitGuard();

  const [newLead, setNewLead] = useState({
    primaryName: "", primaryPhone: "", primaryEmail: "",
    secondaryName: "", secondaryPhone: "", secondaryEmail: "",
    address: "", city: "", state: "FL", zip: "",
    projectType: "", leadSource: "", assignedRep: "", notes: "",
  });
  const isRepRole = role === "sales";
  const currentRepId = isRepRole ? user?.id ?? null : null;
  const currentRep = useMemo(() => {
    if (!isRepRole) return null;
    const resolved = getCurrentRepName(user);
    return resolved || null;
  }, [isRepRole, user]);
  const visibleStages = getVisibleStages();
  const stageNames = visibleStages.map((s) => s.name) as PipelineStage[];
  const activeServerRepFilter = isRepRole ? (currentRepId ?? currentRep ?? "all") : repFilter;
  const serverKanbanLeadState = usePipelineLeadsKanban({
    enabled: serverPagingEnabled && viewMode === "kanban",
    stages: stageNames,
    pageSize: KANBAN_STAGE_PAGE_SIZE,
    search,
    rep: activeServerRepFilter,
    refreshToken: realtimeRefreshToken,
  });
  const serverListLeadState = usePipelineLeadsPage({
    enabled: serverPagingEnabled && viewMode === "list",
    page: serverListPage,
    pageSize: serverListPageSize,
    search,
    rep: activeServerRepFilter,
    stage: "all",
    refreshToken: realtimeRefreshToken,
  });
  const serverSpeedToLeadState = usePipelineSpeedToLeadSummary({
    enabled: serverPagingEnabled,
    refreshToken: realtimeRefreshToken,
  });
  const legacyLeadState = useLeads({ enabled: !serverPagingEnabled });
  const activeLeadState = serverPagingEnabled
    ? (viewMode === "list" ? serverListLeadState : serverKanbanLeadState)
    : legacyLeadState;
  const { leads, loading: leadsLoading, setLeads, createLead } = activeLeadState;
  const isPipelineLoading = leadsLoading || (!serverPagingEnabled && isRefreshing);

  const { resolveMany, getName } = useUserResolver();

  const [activeRepIds, setActiveRepIds] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    supabase
      .from("profiles")
      .select("id")
      .in("role", ["sales", "sales_manager"])
      .eq("is_active", true)
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        setActiveRepIds(data.map((p) => p.id as string));
      });
    return () => { cancelled = true; };
  }, []);

  const reps = useMemo((): string[] => {
    const repSet = new Set<string>(activeRepIds);
    leads.forEach((l) => { if (l.assignedRep) repSet.add(l.assignedRep); });
    return Array.from(repSet).sort();
  }, [leads, activeRepIds]);

  useEffect(() => {
    if (reps.length > 0) resolveMany(reps);
  }, [reps, resolveMany]);

  function getRepDisplayName(repValue: string | null | undefined): string {
    if (!repValue) return "Unassigned";
    return getName(repValue) ?? repValue;
  }

  // Enrich all leads with response metrics + priority
  const enrichedLeads = useMemo((): EnrichedLead[] => {
    return leads.map((l) => {
      const responseMetrics = calculateResponseMetrics(l, conversations, messages);
      const priority = calculateLeadPriority(l, responseMetrics);
      const temp = scoreLeadConversion(l, conversations, messages);
      return { ...l, responseMetrics, priority, temperature: temp.temperature };
    });
  }, [leads, conversations, messages]);

  // Speed-to-lead summary
  const stlSummary = useMemo(() => {
    if (serverPagingEnabled) return serverSpeedToLeadState.summary;
    return calculateSpeedToLeadSummary(leads, conversations, messages);
  }, [serverPagingEnabled, serverSpeedToLeadState.summary, leads, conversations, messages]);

  async function resolveLeadCustomerId(lead: Lead): Promise<string | null> {
    if ((lead as any).customerId) return String((lead as any).customerId);
    const canonicalLeadId = await resolveCanonicalLeadId(lead);
    if (!canonicalLeadId) return null;
    const { data, error } = await LeadService.getById(canonicalLeadId);
    if (error || !data) return null;
    return String((data as any).customer_id ?? "");
  }

  async function resolveCanonicalLeadId(lead: Lead): Promise<string | null> {
    const explicitLeadId = String((lead as any).leadId ?? "").trim();
    if (explicitLeadId) return explicitLeadId;

    const rowId = String((lead as any).id ?? "").trim();
    if (!rowId) return null;

    const leadRow = await LeadService.getById(rowId);
    if (leadRow.data?.id && !leadRow.error) {
      return String(leadRow.data.id);
    }

    const linkedQuoteId = String((lead as any).linkedQuoteId ?? (lead as any).quoteId ?? "").trim();
    if (linkedQuoteId) {
      const { data: quoteRow } = await QuoteService.getById(linkedQuoteId);
      const quoteLeadId = String((quoteRow as any)?.lead_id ?? "").trim();
      if (quoteLeadId) return quoteLeadId;
    }

    return null;
  }

  async function ensureQuoteForLead(lead: Lead, appointmentId?: string): Promise<string | null> {
    if ((lead as any).linkedQuoteId) {
      const linkedQuoteId = String((lead as any).linkedQuoteId);
      if (appointmentId) {
        await QuoteService.update(linkedQuoteId, { appointment_id: appointmentId }).catch((err) => {
          console.warn("Failed to sync appointment_id to linked quote:", err);
        });
      }
      return linkedQuoteId;
    }
    const canonicalLeadId = await resolveCanonicalLeadId(lead);
    if (!canonicalLeadId) {
      console.error("Unable to resolve canonical lead id for quote upsert", lead);
      return null;
    }
    const customerId = await resolveLeadCustomerId(lead);
    const { data, error } = await QuoteService.upsertByLeadId(canonicalLeadId, {
      quote_name: lead.primaryName,
      customer_name: lead.primaryName,
      customer_email: lead.primaryEmail || null,
      customer_phone: lead.primaryPhone || null,
      customer_address: [lead.address, lead.city, `${lead.state} ${lead.zip}`].filter(Boolean).join(", ") || null,
      customer_id: customerId || null,
      organization_id: (lead as any).organizationId || null,
      lead_source: lead.leadSource || null,
      status: "Appointment Scheduled",
      notes: lead.notes || null,
      total_estimate: lead.totalEstimate || 0,
      appointment_id: appointmentId || null,
      project_config: (lead.projectConfig as unknown as Record<string, unknown> | undefined) || {
        consultationCategory: lead.projectType || "Other",
        projectCategory: lead.projectType || "Other",
      },
    });
    if (error || !data?.id) {
      console.error("Failed to ensure quote for lead:", error);
      return null;
    }
    return String(data.id);
  }

  async function persistLeadPatch(
    lead: Lead,
    updates: {
      stage?: PipelineStage;
      appointmentTime?: string;
      assignedRep?: string;
      updatedAt?: string;
      stageHistory?: Lead["stageHistory"];
      activities?: Lead["activities"];
    },
  ) {
    const quoteId = String((lead as any).quoteId ?? "").trim();
    const leadId = String((lead as any).leadId ?? lead.id ?? "").trim();

    const serializedStageHistory = serializeStageHistory(updates.stageHistory);
    const serializedActivities = serializeActivities(updates.activities);

    if (quoteId) {
      const { error } = await QuoteService.update(quoteId, {
        stage: updates.stage,
        stage_history: serializedStageHistory,
      });
      if (error) throw error;
    }

    if (leadId) {
      const { error } = await LeadService.update(leadId, {
        stage: updates.stage,
        appointment_time: updates.appointmentTime,
        assigned_rep: updates.assignedRep,
        stage_history: serializedStageHistory,
        activities: serializedActivities,
      });
      if (error) throw error;
    }
  }

  function openScheduleDialog(leadId: string) {
    const target = leads.find((l) => l.id === leadId);
    const candidateAssigned = String(target?.assignedRep ?? "").trim();
    const defaultRep = isUuid(candidateAssigned)
      ? candidateAssigned
      : (currentRepId && isUuid(currentRepId) ? currentRepId : "");
    setScheduleLeadId(leadId);
    setScheduleDate(format(new Date(Date.now() + 86400000), "yyyy-MM-dd"));
    setScheduleRep(defaultRep);
  }

  const scheduleConflictLead = useMemo(() => {
    if (!scheduleLeadId || !scheduleDate || !scheduleTime || !scheduleRep) return null;
    if (!isUuid(scheduleRep)) return null;
    const target = leads.find((l) => l.id === scheduleLeadId);
    if (!target) return null;

    const candidateIso = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
    const targetRepRaw = String(scheduleRep ?? "").trim().toLowerCase();
    const targetRepDisplay = String(getRepDisplayName(scheduleRep)).trim().toLowerCase();
    const targetRepCandidates = [targetRepRaw, targetRepDisplay].filter((v) => v && v !== "unassigned");

    return (
      leads.find((lead) => {
        if (lead.id === target.id) return false;
        if (!lead.appointmentTime) return false;
        const leadRepRaw = String(lead.assignedRep ?? "").trim().toLowerCase();
        const leadRepDisplay = String(getRepDisplayName(lead.assignedRep)).trim().toLowerCase();
        const sameRep =
          targetRepCandidates.includes(leadRepRaw) ||
          targetRepCandidates.includes(leadRepDisplay);
        if (!sameRep) return false;
        return hasBlockedWindowOverlap(lead.appointmentTime, candidateIso);
      }) ?? null
    );
  }, [scheduleLeadId, scheduleDate, scheduleTime, scheduleRep, leads]);

  // Rep performance for auto-assignment
  const repPerformance = useMemo(() =>
    getRepPerformance(leads, conversations, messages),
    [leads, conversations, messages]
  );

  // Priority queue: unresponded leads sorted by priority
  const priorityQueue = useMemo(() =>
    enrichedLeads
      .filter((l) => l.stage === "Lead" && (!l.responseMetrics.isResponded || l.responseMetrics.slaStatus === "missed" || l.responseMetrics.slaStatus === "at_risk"))
      .sort((a, b) => b.priority.score - a.priority.score)
      .slice(0, 10),
    [enrichedLeads]
  );

  // Filter
  const filtered = useMemo(() => {
    let result = enrichedLeads;
    if (!serverPagingEnabled) {
      if (isRepRole && currentRepId) {
        result = result.filter((l) => l.assignedRep === currentRepId);
      } else if (isRepRole && currentRep) {
        const normalizedCurrentRep = String(currentRep).trim().toLowerCase();
        result = result.filter((l) => String(l.assignedRep ?? "").trim().toLowerCase() === normalizedCurrentRep);
      }
      if (repFilter !== "all") result = result.filter((l) => l.assignedRep === repFilter);
      if (search) {
        const q = search.toLowerCase();
        result = result.filter((l) =>
          l.primaryName.toLowerCase().includes(q) || l.primaryEmail.toLowerCase().includes(q) ||
          l.primaryPhone.includes(q) ||
          l.city.toLowerCase().includes(q) ||
          l.state.toLowerCase().includes(q) ||
          l.zip.toLowerCase().includes(q) ||
          l.address.toLowerCase().includes(q) ||
          getLeadProjectLabel(l).toLowerCase().includes(q)
        );
      }
    }
    return result;
  }, [enrichedLeads, search, repFilter, isRepRole, currentRep, currentRepId, serverPagingEnabled]);
  const clientListPagination = usePagination(filtered, 25);
  const serverListTotalPages = Math.max(1, Math.ceil(serverListLeadState.count / serverListPageSize));
  const serverListSafePage = Math.min(serverListPage, serverListTotalPages - 1);
  const serverListPagination = {
    paged: filtered,
    page: serverListSafePage,
    totalPages: serverListTotalPages,
    totalItems: serverListLeadState.count,
    setPage: setServerListPage,
    hasNext: serverListSafePage < serverListTotalPages - 1,
    hasPrev: serverListSafePage > 0,
    next: () => setServerListPage((p) => Math.min(p + 1, serverListTotalPages - 1)),
    prev: () => setServerListPage((p) => Math.max(p - 1, 0)),
    reset: () => setServerListPage(0),
    pageStart: serverListLeadState.count === 0 ? 0 : serverListSafePage * serverListPageSize + 1,
    pageEnd: Math.min((serverListSafePage + 1) * serverListPageSize, serverListLeadState.count),
    pageSize: serverListPageSize,
    setPageSize: (size: number) => {
      setServerListPageSize(size);
      setServerListPage(0);
    },
  };
  const listPagination = serverPagingEnabled && viewMode === "list" ? serverListPagination : clientListPagination;
  useEffect(() => {
    listPagination.reset();
  }, [search, repFilter, viewMode, serverPagingEnabled]);

  const stageGroups = useMemo(() => {
    const groups: Record<string, EnrichedLead[]> = {};
    stageNames.forEach((s) => (groups[s] = []));
    filtered.forEach((l) => {
      if (groups[l.stage]) groups[l.stage].push(l);
      else { const first = stageNames[0]; if (first) groups[first]?.push(l); }
    });
    return groups;
  }, [filtered, stageNames]);

  function getStageTotal(stageName: string): number {
    if (serverPagingEnabled && viewMode === "kanban") {
      return serverKanbanLeadState.stageCounts[stageName] ?? 0;
    }
    return stageGroups[stageName]?.length || 0;
  }

  function getStageHeaderCount(stageName: PipelineStage): string | number {
    if (serverPagingEnabled && viewMode === "kanban") {
      const meta = serverKanbanLeadState.stageMeta[stageName];
      if (!meta) return 0;
      return `${meta.loaded}/${meta.count}`;
    }
    return stageGroups[stageName]?.length || 0;
  }

  function getContactStatus(lead: Lead) {
    const conv = conversations.find((c) => c.linkedLeadId === lead.id);
    const unread = conv?.unreadCount ?? 0;
    if (!conv) return { label: "No Contact", color: "text-muted-foreground", daysAgo: null, unread };
    const lastMsg = messages.filter((m) => m.conversationId === conv.id).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
    if (!lastMsg) return { label: "No Contact", color: "text-muted-foreground", daysAgo: null, unread };
    const days = differenceInDays(new Date(), new Date(lastMsg.timestamp));
    if (days === 0) return { label: "Today", color: "text-success", daysAgo: 0, unread };
    if (days <= 2) return { label: `${days}d ago`, color: "text-success", daysAgo: days, unread };
    if (days <= 5) return { label: `${days}d ago`, color: "text-warning", daysAgo: days, unread };
    return { label: `${days}d ago`, color: "text-destructive", daysAgo: days, unread };
  }

  const stats = useMemo(() => {
    if (serverPagingEnabled && viewMode === "kanban") {
      const total = stageNames.reduce((sum, stage) => sum + (serverKanbanLeadState.stageCounts[stage] ?? 0), 0);
      const won = serverKanbanLeadState.stageCounts["Closed Won"] ?? 0;
      const lost = serverKanbanLeadState.stageCounts["Closed Lost"] ?? 0;
      return {
        total,
        active: Math.max(total - won - lost, 0),
        newToday: 0,
        won,
        lost,
      };
    }
    const todayIso = new Date().toISOString().split("T")[0];
    const createdOn = (v?: string) => (v ? v.slice(0, 10) : "");
    const active = filtered.filter((l) => l.stage !== "Closed Lost" && l.stage !== "Closed Won");
    return {
      total: filtered.length, active: active.length,
      newToday: filtered.filter((l) => createdOn(l.createdAt) === todayIso).length,
      won: filtered.filter((l) => l.stage === "Closed Won").length,
      lost: filtered.filter((l) => l.stage === "Closed Lost").length,
    };
  }, [filtered, serverPagingEnabled, viewMode, serverKanbanLeadState.stageCounts, stageNames]);

  /**
    * Stage write — proof-based execution.
    * Only callable after StageOverrideDialog confirmation (typed CONFIRM + reason).
    * Drag-and-drop on the kanban funnels here through `pendingDrop` → dialog → confirm.
    */
  function moveLeadToStage(leadId: string, newStage: PipelineStage, overrideReason: string) {
    const target = leads.find((l) => l.id === leadId);
    if (!target || target.stage === newStage) return;
    const nowIso = new Date().toISOString();
    const now = nowIso.split("T")[0];
    const actor = currentRep || "Admin";
    const overrideTag = ` [manual override: ${overrideReason}]`;
    const change = { id: `sh-${Date.now()}`, fromStage: target.stage, toStage: newStage, changedAt: now, changedBy: actor };
    const activity = { id: `la-${Date.now()}`, type: "system" as const, description: `Stage changed from "${target.stage}" to "${newStage}"${overrideTag}`, createdAt: now, createdBy: actor };

    setLeads((prev) =>
      prev.map((l) => {
        if (l.id !== leadId) return l;
        if (l.stage === newStage) return l;
        const lChange = { ...change, fromStage: l.stage };
        const lActivity = { ...activity, description: `Stage changed from "${l.stage}" to "${newStage}"${overrideTag}` };
        toast.success(`Lead moved to ${newStage} (manual override)`);
        return { ...l, stage: newStage, stageHistory: [...l.stageHistory, lChange], activities: [...l.activities, lActivity], updatedAt: now.split("T")[0] };
      })
    );

    // Defer auto-activities to avoid blocking UI (runs in next tick)
    setTimeout(() => {
      const event: AutomationEvent = {
        id: `evt-${Date.now()}`, type: newStage === "Appointment Scheduled" ? "appointment_scheduled" : "lead_stage_changed",
        timestamp: nowIso, payload: { assignedRep: target.assignedRep, fromStage: target.stage, newStage, manualOverride: true, overrideReason },
        sourceModule: "pipeline", entityId: target.id, entityName: target.primaryName, triggeredBy: actor,
      };
      const executions = processAutomationEvent(event, DEFAULT_AUTOMATION_RULES);
      const autoActivities = executions.flatMap((exec) => exec.actions.map((act, i) => ({
        id: `la-auto-${Date.now()}-${i}`, type: "system" as const, description: `⚡ ${act.description}`, createdAt: now.split("T")[0], createdBy: "Automation Engine",
      })));
      if (autoActivities.length > 0) {
        setLeads((prev) =>
          prev.map((l) => {
            if (l.id !== leadId) return l;
            return { ...l, activities: [...l.activities, ...autoActivities] };
          })
        );
      }
    }, 0);

    const nextStageHistory = [...target.stageHistory, change];
    const nextActivities = [...target.activities, activity];
    void persistLeadPatch(target, {
      stage: newStage,
      updatedAt: now,
      stageHistory: nextStageHistory,
      activities: nextActivities,
    }).catch((err) => {
      console.error("Failed to persist manual stage override:", err);
      toast.error("Stage override saved locally, but failed to persist to DB.");
    });
  }

  function handleCreateLead() { guardCreateLead(async () => {
    if (!newLead.primaryName || !newLead.primaryPhone || !newLead.primaryEmail) { toast.error("Name, phone, and email are required"); return; }
    const intakeResult = processIncomingLead(newLead, "manual", leads, DEFAULT_INTEGRITY_CONFIG);
    if (!intakeResult.deliveredToPipeline) {
      toast.warning(`Lead ${intakeResult.disposition}: ${intakeResult.dispositionReason}. Check Lead Integrity dashboard.`);
      setShowNewLead(false);
      setNewLead({ primaryName: "", primaryPhone: "", primaryEmail: "", secondaryName: "", secondaryPhone: "", secondaryEmail: "", address: "", city: "", state: "FL", zip: "", projectType: "", leadSource: "", assignedRep: "", notes: "" });
      return;
    }
    // Auto-assign if no rep selected
    let rep = newLead.assignedRep || currentRepId || "";
    if (!rep && repPerformance.length > 0) {
      const recommendation = recommendAssignment(repPerformance, leads.length);
      rep = recommendation.rep;
      toast.info(`Auto-assigned to ${getRepDisplayName(rep)}: ${recommendation.reason}`);
    }
    if (!rep) rep = reps[0] || "";

    try {
      await createLead({
        primaryName: intakeResult.normalizedData.primaryName,
        primaryPhone: intakeResult.normalizedData.primaryPhone,
        primaryEmail: intakeResult.normalizedData.primaryEmail,
        secondaryName: intakeResult.normalizedData.secondaryName,
        secondaryPhone: intakeResult.normalizedData.secondaryPhone,
        secondaryEmail: intakeResult.normalizedData.secondaryEmail,
        address: intakeResult.normalizedData.address,
        city: intakeResult.normalizedData.city,
        state: intakeResult.normalizedData.state,
        zip: intakeResult.normalizedData.zip,
        projectType: intakeResult.normalizedData.projectType,
        leadSource: intakeResult.normalizedData.leadSource,
        leadSourceId: leadSources.find((source) => source.name === intakeResult.normalizedData.leadSource)?.id,
        assignedRep: rep,
        notes: newLead.notes,
      });
      setShowNewLead(false);
      setNewLead({ primaryName: "", primaryPhone: "", primaryEmail: "", secondaryName: "", secondaryPhone: "", secondaryEmail: "", address: "", city: "", state: "FL", zip: "", projectType: "", leadSource: "", assignedRep: "", notes: "" });
      toast.success(`Lead created (Quality: ${intakeResult.qualityScore})`);
    } catch (err) {
      console.error("Failed to persist lead to database:", err);
      toast.error("Failed to persist lead to database");
    }
  }); }

  async function handleScheduleAppointment() {
    if (!scheduleLeadId || !scheduleDate || !scheduleRep) return;
    if (!isUuid(scheduleRep)) {
      toast.error("Assigned rep is required", {
        description: "Please select a valid sales rep before scheduling.",
      });
      return;
    }
    try {
      // Normalize to UTC ISO so DB + UI round-trip preserves local wall-clock time.
      // Example (ET): 2026-05-02 10:00 local -> 2026-05-02T14:00:00.000Z
      const appointmentDate = new Date(`${scheduleDate}T${scheduleTime}:00`);
      const appointmentTime = appointmentDate.toISOString();
      const appointmentId = crypto.randomUUID();
      const now = new Date().toISOString().split("T")[0];
      const rep = scheduleRep;
      const target = leads.find((l) => l.id === scheduleLeadId);
      if (!target) return;
      const conflictingLead = scheduleConflictLead;
      if (conflictingLead) {
        toast.error("Scheduling conflict", {
          description: `${getRepDisplayName(scheduleRep)} already has an appointment within the 2-hour appointment + 2-hour buffer window.`,
        });
        return;
      }
      let nextStageHistory: Lead["stageHistory"] = target.stageHistory;
      let nextActivities: Lead["activities"] = target.activities;
      setLeads((prev) =>
        prev.map((l) => {
          if (l.id !== scheduleLeadId) return l;
          const newStage: PipelineStage = "Appointment Scheduled";
          const change = { id: `sh-${Date.now()}`, fromStage: l.stage, toStage: newStage, changedAt: now, changedBy: rep };
          const activity = { id: `la-${Date.now()}`, type: "system" as const, description: `Appointment scheduled for ${format(new Date(appointmentTime), "MMM d 'at' h:mm a")}`, createdAt: now, createdBy: rep };
          nextStageHistory = [...l.stageHistory, change];
          nextActivities = [...l.activities, activity];
          return {
            ...l,
            stage: newStage,
            assignedRep: rep,
            appointmentTime,
            stageHistory: [...l.stageHistory, change],
            activities: [...l.activities, activity],
            updatedAt: now,
          };
        })
      );
      await persistLeadPatch(target, {
        stage: "Appointment Scheduled",
        assignedRep: rep,
        appointmentTime,
        updatedAt: now,
        stageHistory: nextStageHistory,
        activities: nextActivities,
      });

      const updatedLead: Lead = {
        ...target,
        stage: "Appointment Scheduled",
        assignedRep: rep,
        appointmentTime,
        updatedAt: now,
        stageHistory: nextStageHistory,
        activities: nextActivities,
      } as Lead;
      const quoteId = await ensureQuoteForLead(updatedLead, appointmentId);
      if (quoteId) {
        setLeads((prev) =>
          prev.map((l) => (l.id === scheduleLeadId ? ({ ...l, linkedQuoteId: quoteId } as any) : l)),
        );
        const canonicalLeadId = await resolveCanonicalLeadId(updatedLead);
        if (canonicalLeadId) {
          await LeadService.update(canonicalLeadId, { linked_quote_id: quoteId }).catch((err) => {
            console.warn("Failed to link lead -> quote after scheduling appointment:", err);
          });
          await AppointmentService.create({
            appointmentId,
            leadId: canonicalLeadId,
            quoteId,
            scheduledAt: appointmentTime,
            status: "scheduled",
            contactName: updatedLead.primaryName,
            contactPhone: updatedLead.primaryPhone,
            contactEmail: updatedLead.primaryEmail || undefined,
            createdBy: rep,
            notes: `Appointment scheduled from pipeline for ${updatedLead.primaryName}`,
          }).catch((err) => {
            console.warn("Failed to persist appointment_result record:", err);
          });
        }
      }

      toast.success("Appointment scheduled");
      setScheduleLeadId(null); setScheduleDate(""); setScheduleTime("10:00"); setScheduleRep("");
    } catch (err) {
      console.error("Failed to persist scheduled appointment:", err);
      toast.error("Appointment scheduled locally, but failed to persist to DB.");
    }
  }

  function getAppointmentInfo(lead: Lead) {
    if (!lead.appointmentTime) return null;
    const apptDate = new Date(lead.appointmentTime);
    const isPast = apptDate < new Date();
    return { date: apptDate, isPast, label: format(apptDate, "MMM d, h:mm a") };
  }

  function getFollowUpInfo(lead: Lead) {
    if (lead.followUpDueDate) { const due = new Date(lead.followUpDueDate); return { label: format(due, "MMM d"), overdue: due < new Date() }; }
    if (lead.followUpType) return { label: lead.followUpType, overdue: false };
    return null;
  }

  function handleDragStart(leadId: string) {
    if (!canOverride) {
      // Field/sales/etc. can't override stage — block drag entirely.
      setDraggedLeadId(null);
      return;
    }
    setDraggedLeadId(leadId);
  }
  function handleDragOver(e: React.DragEvent) { if (canOverride) e.preventDefault(); }
  function handleDrop(stage: PipelineStage) {
    if (!draggedLeadId || !canOverride) { setDraggedLeadId(null); return; }
    const lead = leads.find((l) => l.id === draggedLeadId);
    setDraggedLeadId(null);
    if (!lead || lead.stage === stage) return;
    setPendingDrop({ leadId: lead.id, stage, fromStage: lead.stage, leadName: lead.primaryName });
  }

  /* ═══════ SLA BADGE COMPONENT ═══════ */
  function SLABadge({ status, label }: { status: SLAStatus; label: string }) {
    const cfg = SLA_BADGE_CONFIG[status];
    return (
      <Badge variant="outline" className={cn("text-[10px] h-5 gap-0.5", cfg.color, cfg.bg)}>
        <span>{cfg.icon}</span> {cfg.label}
      </Badge>
    );
  }

  return (
    <TooltipProvider>
      <CockpitPage>
        {/* ── Masthead ── */}
        <CockpitMasthead
          title="Pipeline"
          right={
            <>
              <HeroMetric label="Active" value={stats.active} hero />
              <HeroDivider />
              <HeroMetric label="Won" value={stats.won} tone="good" />
              <HeroDivider />
              <HeroMetric label="Lost" value={stats.lost} tone="bad" />
              <HeroDivider />
              <div className="flex items-center gap-2 self-center">
                <PipelineStageManager />
                <Button size="sm" variant="outline" onClick={() => setViewMode(viewMode === "kanban" ? "list" : "kanban")}>
                  {viewMode === "kanban" ? <List className="h-4 w-4 mr-1" /> : <LayoutGrid className="h-4 w-4 mr-1" />}
                  {viewMode === "kanban" ? "List" : "Board"}
                </Button>
                <Button size="sm" onClick={() => setShowNewLead(true)} {...actionPerms.guardProps("create_lead")}><Plus className="h-4 w-4 mr-1" /> New Lead</Button>
              </div>
            </>
          }
        />

        {/* ── Speed-to-Lead KPI Strip ── */}
        <CockpitKpiStrip
          items={[
            { label: "Avg Response", value: stlSummary.avgResponseTimeSec > 0 ? `${Math.round(stlSummary.avgResponseTimeSec / 60)}m` : "—" },
            { label: "Instant", value: stlSummary.instantCount, tone: "good" },
            { label: "On Time", value: stlSummary.onTimeCount },
            { label: "At Risk", value: stlSummary.atRiskCount, tone: stlSummary.atRiskCount > 0 ? "warn" : "default" },
            { label: "SLA Missed", value: stlSummary.missedCount, tone: stlSummary.missedCount > 0 ? "bad" : "default" },
            { label: "SLA Compliance", value: `${stlSummary.slaComplianceRate}%`, tone: stlSummary.slaComplianceRate >= 80 ? "good" : stlSummary.slaComplianceRate >= 60 ? "warn" : "bad" },
          ]}
        />

        {/* Priority Queue */}
        {priorityQueue.length > 0 && (
          <Card className="border-l-4 border-l-destructive">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Zap className="h-4 w-4 text-destructive" /> Immediate Action Required
                  <Badge className="bg-destructive text-[10px] h-5">{priorityQueue.length}</Badge>
                </CardTitle>
                <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setShowPriorityQueue(!showPriorityQueue)}>
                  {showPriorityQueue ? "Collapse" : "Expand"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Leads awaiting response, sorted by urgency and value</p>
            </CardHeader>
            {showPriorityQueue && (
              <CardContent className="pt-0">
                <div className="space-y-1.5">
                  {priorityQueue.map((lead) => (
                    <div key={lead.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={cn("w-2 h-8 rounded-full shrink-0",
                          lead.priority.label === "Critical" ? "bg-destructive" :
                          lead.priority.label === "High" ? "bg-warning/60" :
                          lead.priority.label === "Medium" ? "bg-warning/40" : "bg-muted-foreground/30"
                        )} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Link to={`/pipeline/${lead.id}`} className="text-sm font-medium text-foreground hover:underline truncate">{lead.primaryName}</Link>
                            <SLABadge status={lead.responseMetrics.slaStatus} label={lead.responseMetrics.slaLabel} />
                            {lead.totalEstimate > 0 && <span className="text-xs font-medium text-foreground">${lead.totalEstimate.toLocaleString()}</span>}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {lead.responseMetrics.slaLabel} · {lead.leadSource} · {getRepDisplayName(lead.assignedRep)}
                            {isGrowthChannelSource(lead.leadSource) && (
                              <span className="ml-1 text-[9px] font-medium text-primary">· Growth Channel</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Tooltip><TooltipTrigger asChild>
                          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => { window.open(`tel:${lead.primaryPhone}`, "_self"); toast.info(`Calling ${lead.primaryName}...`); }}>
                            <Phone className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger><TooltipContent>Call now</TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild>
                          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => navigate(`/pipeline/${lead.id}`)}>
                            <MessageSquare className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger><TooltipContent>Send message</TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild>
                              <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => openScheduleDialog(lead.id)}>
                                <CalendarPlus className="h-3 w-3" />
                              </Button>
                        </TooltipTrigger><TooltipContent>Schedule appointment</TooltipContent></Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Filter bar */}
        <CockpitFilterBar
          search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search leads..." />}
          right={!isRepRole ? (
            <Select value={repFilter} onValueChange={setRepFilter}>
              <SelectTrigger className="w-[180px] h-9"><SelectValue placeholder="All Reps" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reps</SelectItem>
                {reps.map((r) => <SelectItem key={r} value={r}>{getRepDisplayName(r)}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : undefined}
        />

        {/* Stage counts strip */}
        <CockpitKpiStrip
          items={visibleStages.filter((s) => s.type !== "lost").map((stage) => ({
            label: stage.name,
            value: getStageTotal(stage.name),
          }))}
        />

        {/* Kanban View */}
        {viewMode === "kanban" && (
          <div className="flex gap-3 overflow-x-auto pb-4">
            {visibleStages.map((stageConfig) => {
              const stageName = stageConfig.name as PipelineStage;
              return (
              <div key={stageConfig.id} className="min-w-[270px] max-w-[290px] flex-shrink-0"
                onDragOver={handleDragOver} onDrop={() => handleDrop(stageName)}>
                <div className={cn("rounded-t-lg px-3 py-2 border border-b-0 flex items-center justify-between", stageConfig.color)}>
                  <span className="text-xs font-semibold truncate">{stageConfig.name}</span>
                  <Badge variant="secondary" className="text-[10px] h-5">{getStageHeaderCount(stageName)}</Badge>
                </div>
                <div className="border rounded-b-lg bg-muted/30 min-h-[200px] p-2 space-y-2">
                  {(!stageGroups[stageConfig.name] || stageGroups[stageConfig.name].length === 0) ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center mb-2">
                        {isPipelineLoading ? (
                          <Clock className="h-4 w-4 text-muted-foreground/60 animate-spin" />
                        ) : (
                          <User className="h-4 w-4 text-muted-foreground/50" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {isPipelineLoading ? "Loading latest leads..." : "No leads in this stage"}
                      </p>
                    </div>
                  ) : null}
                  {stageGroups[stageConfig.name]?.map((lead) => {
                    const contactStatus = getContactStatus(lead);
                    const slaStatus = lead.responseMetrics.slaStatus;
                    return (
                    <div
                      key={lead.id}
                      draggable={canOverride}
                      onDragStart={canOverride ? () => handleDragStart(lead.id) : undefined}
                      title={canOverride ? "Drag to override stage (requires confirmation)" : "Stages advance automatically as work is completed"}
                      className={cn(
                        "bg-card border rounded-lg p-3 hover:shadow-md transition-shadow group",
                        canOverride ? "cursor-grab active:cursor-grabbing" : "cursor-default",
                        slaStatus === "missed" && lead.stage === "Lead" && "ring-1 ring-destructive/40",
                        slaStatus === "at_risk" && lead.stage === "Lead" && "ring-1 ring-warning",
                      )}>
                      <div className="flex items-start justify-between gap-1">
                        <Link to={`/pipeline/${lead.id}`} className="text-sm font-medium text-foreground hover:underline truncate">{lead.primaryName}</Link>
                        {canOverride
                          ? <GripVertical className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0 opacity-0 group-hover:opacity-100" />
                          : <Lock className="h-3 w-3 text-muted-foreground/30 shrink-0 opacity-0 group-hover:opacity-100" />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {getLeadProjectLabel(lead)}
                        {isGrowthChannelSource(lead.leadSource) && (
                          <span className="ml-1 text-[9px] text-primary font-medium">· Growth Channel</span>
                        )}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{getLeadLocationLabel(lead)}</span>
                      </div>
                      {/* SLA + Temperature + Status + Outcome indicators */}
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {lead.stage === "Lead" && <SLABadge status={slaStatus} label={lead.responseMetrics.slaLabel} />}
                        {lead.stage === "Closed Won" && lead.wonOutcome && lead.wonOutcome !== "net" && (
                          <Badge className={cn("text-[9px] h-5 border-0", WON_OUTCOME_COLORS[lead.wonOutcome])}>{WON_OUTCOME_LABELS[lead.wonOutcome]}</Badge>
                        )}
                        {(() => { const tc = TEMP_CONFIG[lead.temperature]; return (
                          <Badge variant="outline" className={cn("text-[10px] h-5 gap-0.5", tc.color, tc.bg)}>{tc.icon} {tc.label}</Badge>
                        ); })()}
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-muted-foreground/50" />
                          <span className={cn("text-[10px] font-medium", contactStatus.color)}>{contactStatus.label}</span>
                        </div>
                        {contactStatus.unread > 0 && (
                          <Link
                            to={`/pipeline/${lead.id}#messages`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-0.5 rounded-full bg-warning/10 text-warning border border-warning/30 px-1.5 h-5 text-[10px] font-medium hover:bg-warning/20 transition-colors"
                            title={`${contactStatus.unread} unread message${contactStatus.unread === 1 ? "" : "s"}`}
                          >
                            <MessageSquare className="h-2.5 w-2.5" />
                            {contactStatus.unread}
                          </Link>
                        )}
                        {(() => {
                          const appt = getAppointmentInfo(lead);
                          if (appt) return (
                            <div className="flex items-center gap-1">
                              <Calendar className={cn("h-3 w-3", appt.isPast ? "text-muted-foreground" : "text-success")} />
                              <span className="text-[10px] text-muted-foreground">{appt.label}</span>
                            </div>
                          );
                          return null;
                        })()}
                        {(() => {
                          const fu = getFollowUpInfo(lead);
                          if (fu) return (
                            <div className="flex items-center gap-1">
                              <AlertTriangle className={cn("h-3 w-3", fu.overdue ? "text-destructive" : "text-warning")} />
                              <span className={cn("text-[10px]", fu.overdue ? "text-destructive font-medium" : "text-muted-foreground")}>
                                {fu.overdue ? "Overdue" : fu.label}
                              </span>
                            </div>
                          );
                          return null;
                        })()}
                      </div>
                      {/* Action buttons */}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
                        <div className="flex gap-1">
                          <Tooltip><TooltipTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-6 w-6"
                              onClick={(e) => { e.stopPropagation(); window.open(`tel:${lead.primaryPhone}`, "_self"); toast.info(`Calling ${lead.primaryName}...`); }}>
                              <Phone className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger><TooltipContent>Call lead</TooltipContent></Tooltip>
                          <Tooltip><TooltipTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-6 w-6"
                              onClick={(e) => { e.stopPropagation(); navigate(`/pipeline/${lead.id}`); }}>
                              <MessageSquare className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger><TooltipContent>Message / View</TooltipContent></Tooltip>
                          {lead.stage === "Lead" && (
                            <Tooltip><TooltipTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-6 w-6"
                                onClick={(e) => { e.stopPropagation(); openScheduleDialog(lead.id); }}>
                                <CalendarPlus className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger><TooltipContent>Schedule appointment</TooltipContent></Tooltip>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground">{getRepDisplayName(lead.assignedRep)}</span>
                          {lead.linkedQuoteId && <Link to={`/opportunities/${lead.linkedQuoteId}`} onClick={(e) => e.stopPropagation()}><Badge variant="outline" className="text-[9px] h-4 px-1"><FileText className="h-2.5 w-2.5" /></Badge></Link>}
                          {lead.linkedJobId && <Link to={`/jobs/${lead.linkedJobId}`} onClick={(e) => e.stopPropagation()}><Badge variant="outline" className="text-[9px] h-4 px-1"><Briefcase className="h-2.5 w-2.5" /></Badge></Link>}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                  {serverPagingEnabled && (() => {
                    const meta = serverKanbanLeadState.stageMeta[stageName];
                    if (!meta || meta.count === 0) return null;
                    return (
                      <div className="rounded-lg border border-dashed bg-background/50 px-2 py-2 text-center">
                        <p className="text-[10px] text-muted-foreground mb-2">
                          Showing {meta.loaded} of {meta.count}
                        </p>
                        {meta.hasMore ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 w-full text-xs"
                            disabled={meta.loading}
                            onClick={() => void serverKanbanLeadState.loadMore(stageName)}
                          >
                            {meta.loading ? (
                              <>
                                <Clock className="h-3 w-3 mr-1 animate-spin" /> Loading...
                              </>
                            ) : (
                              `Load ${Math.min(KANBAN_STAGE_PAGE_SIZE, meta.count - meta.loaded)} more`
                            )}
                          </Button>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">All loaded</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* List View */}
        {viewMode === "list" && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-muted/50">
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Lead</th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Stage</th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Response</th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Source</th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Rep</th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Location</th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Appointment</th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Actions</th>
                  </tr></thead>
                  <tbody>
                    {listPagination.paged.map((lead) => (
                      <tr key={lead.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/pipeline/${lead.id}`)}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-foreground">{lead.primaryName}</p>
                          <p className="text-xs text-muted-foreground">{lead.primaryPhone}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={cn("text-[10px]", getStageColor(lead.stage))}>{lead.stage}</Badge>
                          {lead.stage === "Closed Won" && lead.wonOutcome && lead.wonOutcome !== "net" && (
                            <Badge className={cn("ml-1 text-[9px] h-4 border-0", WON_OUTCOME_COLORS[lead.wonOutcome])}>{WON_OUTCOME_LABELS[lead.wonOutcome]}</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <SLABadge status={lead.responseMetrics.slaStatus} label={lead.responseMetrics.slaLabel} />
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {lead.leadSource}
                          {isGrowthChannelSource(lead.leadSource) && (
                            <Badge variant="outline" className="ml-1.5 text-[9px] h-4 bg-primary/5 text-primary border-primary/15">Growth</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{getRepDisplayName(lead.assignedRep)}</td>
                        <td className="px-4 py-3 text-muted-foreground">{getLeadLocationLabel(lead)}</td>
                        <td className="px-4 py-3">
                          {(() => { const appt = getAppointmentInfo(lead); return appt ? <span className="text-xs text-muted-foreground">{appt.label}</span> : <span className="text-xs text-muted-foreground/50">—</span>; })()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); window.open(`tel:${lead.primaryPhone}`, "_self"); }}><Phone className="h-3 w-3" /></Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); navigate(`/pipeline/${lead.id}`); }}><MessageSquare className="h-3 w-3" /></Button>
                            {lead.stage === "Lead" && (
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); openScheduleDialog(lead.id); }}><CalendarPlus className="h-3 w-3" /></Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar {...listPagination} />
              {filtered.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                    {isPipelineLoading ? (
                      <Clock className="h-6 w-6 text-muted-foreground/60 animate-spin" />
                    ) : (
                      <Search className="h-6 w-6 text-muted-foreground/50" />
                    )}
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    {isPipelineLoading ? "Loading latest pipeline..." : "No leads found"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isPipelineLoading ? "Fetching the newest data from DB" : "Try adjusting your search or filters"}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* New Lead Dialog */}
        <Dialog open={showNewLead} onOpenChange={setShowNewLead}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Primary Name *</Label><Input value={newLead.primaryName} onChange={(e) => setNewLead({ ...newLead, primaryName: e.target.value })} className="h-9" /></div>
                <div><Label className="text-xs">Secondary Name</Label><Input value={newLead.secondaryName} onChange={(e) => setNewLead({ ...newLead, secondaryName: e.target.value })} className="h-9" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Primary Phone *</Label><Input type="tel" inputMode="numeric" value={newLead.primaryPhone} onChange={(e) => setNewLead({ ...newLead, primaryPhone: formatPhoneNumber(e.target.value) })} className="h-9" /></div>
                <div><Label className="text-xs">Primary Email *</Label><Input type="email" value={newLead.primaryEmail} onChange={(e) => setNewLead({ ...newLead, primaryEmail: e.target.value })} className="h-9" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Secondary Phone</Label><Input type="tel" inputMode="numeric" value={newLead.secondaryPhone} onChange={(e) => setNewLead({ ...newLead, secondaryPhone: formatPhoneNumber(e.target.value) })} className="h-9" /></div>
                <div><Label className="text-xs">Secondary Email</Label><Input type="email" value={newLead.secondaryEmail} onChange={(e) => setNewLead({ ...newLead, secondaryEmail: e.target.value })} className="h-9" /></div>
              </div>
              <div><Label className="text-xs">Address</Label><Input value={newLead.address} onChange={(e) => setNewLead({ ...newLead, address: e.target.value })} className="h-9" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">City</Label><Input value={newLead.city} onChange={(e) => setNewLead({ ...newLead, city: e.target.value })} className="h-9" /></div>
                <div><Label className="text-xs">State</Label><Input value={newLead.state} onChange={(e) => setNewLead({ ...newLead, state: e.target.value })} className="h-9" /></div>
                <div><Label className="text-xs">ZIP</Label><Input value={newLead.zip} onChange={(e) => setNewLead({ ...newLead, zip: e.target.value })} className="h-9" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Project Type</Label>
                  <Select value={newLead.projectType} onValueChange={(v) => setNewLead({ ...newLead, projectType: v })}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{PROJECT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Lead Source</Label>
                  <Select value={newLead.leadSource} onValueChange={(v) => setNewLead({ ...newLead, leadSource: v })}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {!isRepRole && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Assign to Rep</Label>
                    {repPerformance.length > 0 && !newLead.assignedRep && (
                      <span className="text-[10px] text-primary flex items-center gap-1"><UserPlus className="h-3 w-3" /> Auto-assign on create</span>
                    )}
                  </div>
                  <Select value={newLead.assignedRep} onValueChange={(v) => setNewLead({ ...newLead, assignedRep: v })}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Auto-assign..." /></SelectTrigger>
                    <SelectContent>{reps.map((r) => <SelectItem key={r} value={r}>{getRepDisplayName(r)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label className="text-xs">Notes</Label><Textarea value={newLead.notes} onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })} rows={2} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewLead(false)}>Cancel</Button>
              <Button onClick={handleCreateLead} disabled={isCreatingLead.current || !newLead.primaryName || !newLead.primaryPhone || !newLead.primaryEmail}>
                {isCreatingLead.current ? "Creating…" : "Create Lead"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Schedule Appointment Dialog */}
        <Dialog open={!!scheduleLeadId} onOpenChange={(open) => { if (!open) setScheduleLeadId(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Schedule Appointment</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Assigned Rep</Label>
                <Select value={scheduleRep} onValueChange={setScheduleRep}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select rep..." /></SelectTrigger>
                  <SelectContent>
                    {Array.from(new Set([
                      ...reps.filter((r) => isUuid(r)),
                      currentRepId && isUuid(currentRepId) ? currentRepId : "",
                      (() => {
                        const assigned = String(leads.find((l) => l.id === scheduleLeadId)?.assignedRep ?? "").trim();
                        return isUuid(assigned) ? assigned : "";
                      })(),
                    ].filter(Boolean))).map((r) => (
                      <SelectItem key={r} value={r}>{getRepDisplayName(r)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Date</Label><Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="h-9" /></div>
              <div><Label className="text-xs">Time</Label><Input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="h-9" /></div>
              {scheduleConflictLead && (
                <div className="rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs text-warning">
                  Conflict: {getRepDisplayName(scheduleRep)} is already booked for {scheduleConflictLead.primaryName} within the 2-hour appointment + 2-hour buffer window.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setScheduleLeadId(null); setScheduleRep(""); }}>Cancel</Button>
              <Button onClick={handleScheduleAppointment} disabled={!scheduleDate || !scheduleRep || !isUuid(scheduleRep) || !!scheduleConflictLead}>Schedule</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Stage override gate — drag-and-drop on the kanban funnels here. Proof-based execution. */}
        <StageOverrideDialog
          open={pendingDrop !== null}
          onOpenChange={(open) => !open && setPendingDrop(null)}
          fromStage={pendingDrop?.fromStage ?? ""}
          toStage={pendingDrop?.stage ?? ""}
          entityLabel="Lead"
          entityName={pendingDrop?.leadName}
          onConfirm={(reason) => {
            if (pendingDrop) moveLeadToStage(pendingDrop.leadId, pendingDrop.stage, reason);
            setPendingDrop(null);
          }}
        />
      </CockpitPage>
    </TooltipProvider>
  );
}
